// Story 8.6 (ADO-2015), D-8: why the last send at this depot failed, on the Order Review page.
// The DS error `Banner` with the message `useOrderSubmissionStore` holds.
//
// It is its own component, not part of `SubmitOrderCard`, because the card is hidden on an empty
// cart and the message must still be seen there: a `cart-changed` 409 re-opens the depot's
// current cart, and that cart can be empty (it was sent elsewhere, or emptied). The page mounts it
// whenever the send failed at this depot, whatever the lines.
//
// "Try again" replays the same attempt, so it is offered only when the failure is retryable (a
// network failure, a timeout, a 5xx or a transient 408/425/429) AND the page still has a cart to
// send (`canRetry`: the Submit card is shown). A refusal names what to change instead, and the
// Submit button sends again once it is changed. A refusal of the customer order reference is not
// shown here at all: the Order footer shows it on the reference field (Story 8.14, D-8).

import { useCallback } from 'react';
import { Banner } from '../../design-system';
import { cartTargetKey } from '../../api/cart-target';
import { useCartTargetAt } from '../../basket/sessionCart';
import { useOrderSubmissionStore } from '../../basket/useOrderSubmissionStore';
import styles from './SubmitOrderFailure.module.css';

export interface SubmitOrderFailureProps {
  readonly depotId: string;
  /** False when there is nothing to send (no lines): a retry then has no cart to replay. */
  readonly canRetry: boolean;
}

export function SubmitOrderFailure({ depotId, canRetry }: SubmitOrderFailureProps) {
  const error = useOrderSubmissionStore((s) =>
    s.phase === 'failed' && s.depotId === depotId ? s.error : null,
  );
  const submit = useOrderSubmissionStore((s) => s.submit);
  // Story 8.13 (ADO-2022), D-6/D-7: whether the Fulfilment card is on screen to show a refused
  // choice on its fields (it registers what it shows while mounted).
  const cart = useCartTargetAt(depotId);
  const cardShown = useOrderSubmissionStore(
    (s) => s.fulfilmentShownByCart[cart === null ? depotId : cartTargetKey(cart)] !== undefined,
  );
  // `submit` is itself a no-op while the basket is not in a sendable state (`canSubmitOrder`).
  const retry = useCallback(() => void submit(depotId), [submit, depotId]);

  // A refused reference is shown on the reference field (the Order footer), and announced once,
  // by that field's alert; the banner stays out of it. So is a refused fulfilment choice while
  // the Fulfilment card is there to show it; with no card, the banner carries its messages.
  // Story 8.3 (ADO-2012), card 09: a cart that changed while it was reviewed has its own banner.
  if (error === null || error.field !== undefined || error.changedWhileReviewing !== undefined) {
    return null;
  }
  const fieldMessages = error.fields?.map((refusal) => refusal.message) ?? [];
  if (fieldMessages.length > 0 && cardShown) return null;
  return (
    <Banner type="error" title="Your order was not sent" className={styles.error ?? ''}>
      <span>{fieldMessages.length > 0 ? fieldMessages.join(' ') : error.message}</span>
      {error.retryable && canRetry ? (
        <>
          {' '}
          <button type="button" className={styles.retry} onClick={retry}>
            Try again
          </button>
        </>
      ) : null}
    </Banner>
  );
}
